// Field-level diff of two PQ stores, for the planning change log.
//
// "What has planning changed since the advisor finished?" is only answerable
// if changes can be named — section, row, field, old value, new value — rather
// than "the record differs". This walks the form slices of two stores and
// produces exactly that, one entry per changed leaf.
//
// Ground rules, each of which exists because its absence would make the log
// lie:
//   - Rows are matched by _uid, so editing a row, adding one and removing one
//     are three different stories, and REORDERING rows tells no story at all.
//   - `_`-prefixed bookkeeping keys are invisible. The auto-source syncs stamp
//     them on rows at load; a log that reported those would be manufacturing
//     edits nobody made (the same principle as the payload-based save digest).
//   - A blank row is not data. Rendering seeds one into every empty table, so
//     a seeded row appearing or a blank row vanishing is not a change.
//   - '' , null and undefined are the same "empty" — loading a record fills
//     absent keys with '', and that must not read as an edit.
//
// Kept pure and dependency-free so it can be unit-tested in isolation.

// The store slices the diff walks. pqArchive and planningLog are absent by
// construction: the log must never diff itself or the snapshot it is measured
// against, or every save would beget another entry.
export const PQ_FORM_KEYS = [
  'family',
  'contact',
  'employment',
  'goals',
  'relationships',
  'assets',
  'liabilities',
  'insurance',
  'income',
  'taxesExpenses',
  '_flags',
  '_assetChecks',
];

const SECTION_LABELS = {
  family: 'Household',
  contact: 'Contact',
  employment: 'Employment',
  goals: 'Goals & Notes',
  relationships: 'Relationships',
  assets: 'Assets',
  liabilities: 'Liabilities',
  insurance: 'Insurance',
  income: 'Income',
  taxesExpenses: 'Taxes & Expenses',
  _flags: 'Household',
  _assetChecks: 'Assets',
};

// Labels for the table keys and field keys an entry is most likely to name.
// Anything not listed falls back to the camelCase prettifier below.
const KEY_LABELS = {
  taxDeferred: 'Tax-Deferred Retirement',
  roth: 'Tax-Free Roth',
  taxable: 'Taxable Non-Retirement',
  cashCd: "Cash & CD's",
  plan529: '529 Plans',
  hsa: 'HSA',
  realEstate: 'Real Estate',
  businessOther: 'Business & Other',
  items: 'Liabilities',
  policies: 'Insurance Policies',
  professionals: 'Professionals',
  socialSecurity: 'Social Security',
  noneOf: 'Marked "None"',
  estatePlan: 'Estate Plan',
  livingExpenses: 'Living Expenses',
  livingExpensesNotes: 'Living Expenses Notes',
  marketValue: 'Market Value',
  remainingLoan: 'Remaining Loan',
  incomeEBT: 'Income (EBT)',
  variableFixed: 'Type',
  dob: 'DOB',
  cola: 'COLA %',
  maxContribution: 'Maxes Out',
  termYears: 'Term (Years)',
};

const ACRONYMS = new Set(['dob', 'ssn', 'hsa', 'poa', 'ltc', 'cd', 'ebt', 'fica', 'id']);

// camelCase → human label: 'client1FirstName' → 'Client 1 First Name'.
export function labelForKey(key) {
  if (KEY_LABELS[key]) return KEY_LABELS[key];
  return String(key)
    .replace(/^_/, '')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .split(/[\s_]+/)
    .map(w => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

// Empty-equivalence: '' == null == undefined == whitespace.
const norm = v => {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'true' : '';
  return String(v).trim();
};

const isScalar = v =>
  v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

// Arrays of scalars (estatePlan, noneOf) are sets as far as the log is
// concerned: order carries no meaning, membership does.
const isScalarArray = a => Array.isArray(a) && a.every(isScalar);
const setKey = a => (a || []).map(norm).filter(Boolean).sort().join(', ');

function rowHasData(row) {
  if (!row || typeof row !== 'object') return false;
  return Object.entries(row).some(([k, v]) => !k.startsWith('_') && norm(v) !== '');
}

const rowUidOf = row => (row && (row._uid || row._autoKey)) || null;

// What to call a row in the log: the first identifying thing an advisor typed.
export function rowNameOf(row) {
  if (!row || typeof row !== 'object') return '';
  for (const k of ['description', 'custodian', 'company', 'name', 'insured', 'owner', 'type']) {
    const v = norm(row[k]);
    if (v) return v;
  }
  return '';
}

// The diff. Returns entries:
//   { section, area, rowUid, rowName, field, label, kind, before, after }
//   kind: 'changed' | 'added' | 'removed'
// `area` is the dotted store path of the containing table/object
// (e.g. 'assets.roth'); `field` the leaf key; label is human-readable.
export function diffPq(before = {}, after = {}) {
  const out = [];
  for (const key of PQ_FORM_KEYS) {
    walk(before?.[key], after?.[key], key, SECTION_LABELS[key] || labelForKey(key), key, out);
  }
  return out;
}

function walk(a, b, area, section, keyForLabel, out) {
  if (a === undefined && b === undefined) return;

  // Row tables.
  if (Array.isArray(a) || Array.isArray(b)) {
    const arrA = Array.isArray(a) ? a.filter(Boolean) : [];
    const arrB = Array.isArray(b) ? b.filter(Boolean) : [];

    if (isScalarArray(arrA) && isScalarArray(arrB)) {
      const ka = setKey(arrA);
      const kb = setKey(arrB);
      if (ka !== kb) {
        out.push({
          section, area, rowUid: null, rowName: '',
          field: keyForLabel, label: labelForKey(keyForLabel),
          kind: 'changed', before: ka, after: kb,
        });
      }
      return;
    }

    const tableLabel = labelForKey(keyForLabel);
    const byUidB = new Map(arrB.map(r => [rowUidOf(r), r]));
    const seen = new Set();

    arrA.forEach((rowA, i) => {
      const uid = rowUidOf(rowA);
      const rowB = uid != null && byUidB.has(uid) ? byUidB.get(uid) : undefined;
      if (rowB === undefined) {
        // Gone. A blank row vanishing (unseeding) is not a change.
        if (rowHasData(rowA)) {
          out.push({
            section, area, rowUid: uid, rowName: rowNameOf(rowA),
            field: null, label: tableLabel,
            kind: 'removed', before: rowSummary(rowA), after: '',
          });
        }
        return;
      }
      seen.add(uid);
      diffRow(rowA, rowB, area, section, uid, out);
      void i;
    });

    arrB.forEach(rowB => {
      const uid = rowUidOf(rowB);
      if (uid != null && seen.has(uid)) return;
      const existedBefore = uid != null && arrA.some(r => rowUidOf(r) === uid);
      if (existedBefore) return; // already diffed above
      // New row. A seeded blank is not a change.
      if (rowHasData(rowB)) {
        out.push({
          section, area, rowUid: uid, rowName: rowNameOf(rowB),
          field: null, label: tableLabel,
          kind: 'added', before: '', after: rowSummary(rowB),
        });
      }
    });
    return;
  }

  // Nested objects (contact, goals, a liabilities.items wrapper...).
  if ((a && typeof a === 'object') || (b && typeof b === 'object')) {
    const oa = a && typeof a === 'object' ? a : {};
    const ob = b && typeof b === 'object' ? b : {};
    const keys = new Set([...Object.keys(oa), ...Object.keys(ob)]);
    for (const k of keys) {
      // Bookkeeping keys are invisible EXCEPT at the top level, where two whole
      // slices (_flags, _assetChecks) legitimately start with an underscore —
      // walk() is only ever entered with those from diffPq itself.
      if (k.startsWith('_')) continue;
      walk(oa[k], ob[k], `${area}.${k}`, section, k, out);
    }
    return;
  }

  // Scalars.
  const na = norm(a);
  const nb = norm(b);
  if (na !== nb) {
    out.push({
      section, area: area.slice(0, area.lastIndexOf('.') === -1 ? area.length : area.lastIndexOf('.')),
      rowUid: null, rowName: '',
      field: keyForLabel, label: labelForKey(keyForLabel),
      kind: 'changed', before: na, after: nb,
    });
  }
}

function diffRow(rowA, rowB, area, section, uid, out) {
  const rowName = rowNameOf(rowB) || rowNameOf(rowA);
  const keys = new Set([...Object.keys(rowA), ...Object.keys(rowB)]);
  for (const k of keys) {
    if (k.startsWith('_')) continue;
    const va = rowA[k];
    const vb = rowB[k];
    if (isScalarArray(va) && isScalarArray(vb)) {
      if (setKey(va) !== setKey(vb)) {
        out.push({
          section, area, rowUid: uid, rowName,
          field: k, label: labelForKey(k),
          kind: 'changed', before: setKey(va), after: setKey(vb),
        });
      }
      continue;
    }
    const na = norm(va);
    const nb = norm(vb);
    if (na !== nb) {
      out.push({
        section, area, rowUid: uid, rowName,
        field: k, label: labelForKey(k),
        kind: 'changed', before: na, after: nb,
      });
    }
  }
}

// One-line description of a whole row, for added/removed entries: the row's
// non-empty values, name first.
function rowSummary(row) {
  const name = rowNameOf(row);
  const parts = [];
  for (const [k, v] of Object.entries(row)) {
    if (k.startsWith('_')) continue;
    const nv = norm(v);
    if (!nv || nv === name) continue;
    parts.push(`${labelForKey(k)}: ${nv}`);
  }
  return [name, parts.join(', ')].filter(Boolean).join(' · ');
}

// The slice of the store the snapshot freezes and the log walks — shared so
// the two can never disagree about what "the PQ" is. Deep-copied so later
// edits cannot reach back into a frozen snapshot through shared references.
export function pqFormSlice(data = {}) {
  const slice = {};
  for (const k of PQ_FORM_KEYS) {
    if (data[k] !== undefined) slice[k] = data[k];
  }
  try {
    return JSON.parse(JSON.stringify(slice));
  } catch {
    return {};
  }
}
